/**
 * 작성자 : 홍선영
 * 날짜 : 2024.06.26
 * 기능 : 근로자 출입 현황 대시보드 컴포넌트 (다중현장, 인원만)
 */

import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PulseLoader } from 'react-spinners';
import { useQuery } from '@tanstack/react-query';

import { apiGet } from '../../services/_common';
import IssueGuide from '../IssueGuide';
import Slider from 'react-slick';
import { sortArrayByTimeDesc } from '../../utils/arraySortByTimeDescOrder';
import styled from 'styled-components';

const Root = styled.div<{ size: 'small' | 'large' }>`
  display: flex;
  flex-direction: column;
  height: 100%;
  .header {
    width: 100%;
    height: 8rem;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: ${({ size }) => (size === 'small' ? '2.5rem' : '3.25rem')};
    font-weight: 700;
    gap: 1rem;
    gap: 2rem;
    color: var(--widget-text-primary);
    .badge {
      font-size: 3.5rem;
      padding: 2rem;
      background-color: var(--widget-theme-tonal-blue);
      color: var(--widget-primary);
      // background-color: var(--board);
      // color: var(--widget-theme-darkblue);
      border-radius: 2rem;
    }
  }
  .body {
    width: 100%;
    height: 20rem;
    flex-grow: 1;
    padding: 0 1rem;
    margin-bottom: 2rem;
    .slick-slider {
      height: 100%;
      .slick-arrow {
        display: none !important;
      }
      .slick-list {
        height: 100%;
        overflow: visible;
        .slick-track {
          height: 100%;
          .slick-slide {
            padding: 0 0.5rem; // 좌우 패딩으로 간격 생성

            > div {
              height: 100%;
            }
          }
        }
      }
    }
    .card-container {
      height: 100%;
      .card {
        height: 100%;
        overflow: hidden;
        border-radius: 1rem;
        display: flex;
        flex-direction: column;
        /* background-color: var(--board); */
        .title {
          height: 7rem;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 3rem;
          font-weight: 700;
          border-radius: 0.5rem 0 0;
          color: var(--white);
          background-color: var(--widget-primary);
          line-height: 1;
          text-align: center;
          > span {
            font-weight: 700;
            word-break: keep-all;
            overflow-wrap: break-word;
            white-space: normal;
            /* 필요시 width: 100% 추가 */
          }
        }
        .count {
          flex-grow: 1;
          height: 14.75rem;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 7rem;
          color: var(--widget-text-primary);
          font-weight: 700;
          background-color: var(--widget-sub-bg);

          &.small {
            background-color: var(--widget-bg);
          }
        }
      }
    }
  }
  &.narrow {
    &.narrow-small {
      .body {
        padding: 0;
      }
      .slick-slider .slick-list .slick-track .slick-slide {
        padding: 0;
      }
    }
    .body {
      padding: 0 0.5rem;
      margin: ${({ size }) => (size === 'large' ? '0 0 1rem 0' : '0')};
      .card-container {
        .card {
          .title {
            > span {
              font-size: 2.2rem;
              width: ${({ size }) => (size === 'small' ? '' : '12rem')};
              line-height: 1.2;
              font-weight: 700;
              padding: ${({ size }) => (size === 'small' ? '0 2rem' : '')};
            }
          }
        }
      }
      .count {
        background-color: var(--widget-bg);
      }
    }
  }
`;

interface Iprops {
  name: string;
  userInfo: any;
  refetchInterval: number;
  uniqueKey: string;
  slidesToShow: number;
  size: 'small' | 'large';
}

export const WorkerAttendCountMultiSiteExit = ({ name, userInfo, refetchInterval, uniqueKey, slidesToShow, size = 'large' }: Iprops) => {
  const { t } = useTranslation();
  const tabRefs = useRef<(any | null)[]>([]);
  const [tabList, setTabList] = useState([{ msCd: userInfo.sCd, msName: '당 현장', sliderIndex: 0, count: null }]);

  // 다중현장목록 조회
  const {
    data: msite,
    isLoading: isLoadingMsite,
    isError: isErrorMsite,
    isFetched: isFetchedMsite,
    isRefetching: isRefetchingMsite,
  } = useQuery(['msiteGet', userInfo.hCd, userInfo.sCd], () => fetchMsiteData(), {
    enabled: !!userInfo.hCd && !!userInfo.sCd && userInfo.sCd !== '00000',
    refetchInterval,
    refetchIntervalInBackground: true,
  });

  // 근로자 출입현황 조회
  const { data, isLoading, isError } = useQuery([`attendMultiexitGet_${uniqueKey}`, userInfo.hCd, userInfo.sCd], () => fetchData(), {
    enabled: !!userInfo.hCd && !!userInfo.sCd && userInfo.sCd !== '00000' && !!isFetchedMsite && isRefetchingMsite === false,
    refetchInterval,
    refetchIntervalInBackground: true,
  });

  const fetchMsiteData = async () => {
    try {
      const req = { hCd: userInfo.hCd, sCd: userInfo.sCd };
      const res = await apiGet({ path: `/site/msite`, req });
      const newArray = res.data.data?.filter((el: any) => el.useYn === 'Y')?.map((el2: any) => ({ msCd: el2.msCd, msName: el2.msName }));

      // 현장코드로 정렬
      newArray.sort((a: any, b: any) => {
        const numA = parseInt(a.msCd.substring(1), 10);
        const numB = parseInt(b.msCd.substring(1), 10);
        return numA - numB;
      });
      const addIndex = newArray.map((el: any, i: number) => ({ ...el, sliderIndex: i + 1 }));
      const addMyMsite = [{ msCd: userInfo.sCd, msName: '당 현장', sliderIndex: 0 }, ...addIndex];
      return addMyMsite;
    } catch (error) {
      console.error(error);
      throw new Error('error');
    }
  };

  const fetchData = async () => {
    try {
      if (isFetchedMsite && !isRefetchingMsite && msite) {
        const req = { hCd: userInfo.hCd, sCd: userInfo.sCd };
        const res = await apiGet({ path: `/attend/multiexit/summary`, req });
        const { siteSummary = [], totalCount = 0 } = res.data.data || {};

        const updatedTabList = msite.map((item: any) => {
          const match = siteSummary.find((s: any) => s.msCd === item.msCd);
          return { ...item, msName: match?.msName || item.msName, count: match?.count ?? 0 };
        });
        setTabList(updatedTabList);
        return { totalWorkers: totalCount };
      }
      return null;
    } catch (error) {
      console.error(error);
      throw new Error('error');
    }
  };

  const groupBy = (array: any, key: any) => {
    return array.reduce((result: any, currentValue: any) => {
      (result[currentValue[key]] = result[currentValue[key]] || []).push(currentValue);
      return result;
    }, {});
  };

  const settings = {
    dots: false,
    dotsClass: 'slick-dots slick-thumb',
    infinite: true,
    speed: 1000,
    slidesToShow: tabList.length <= slidesToShow ? tabList.length : slidesToShow,
    slidesToScroll: 1,
    autoplay: tabList.length > slidesToShow,
    autoplaySpeed: 5 * 1000,
  };

  if (isError || isErrorMsite) return <IssueGuide />;
  if (isLoading || isLoadingMsite || !settings) {
    return (
      <div className='centered-content'>
        <PulseLoader color='rgb(0, 122, 255)' size='10px' />
      </div>
    );
  }

  return (
    <Root size={size} className={slidesToShow !== 5 ? (slidesToShow === 1 ? 'narrow narrow-small' : 'narrow') : ''}>
      {size === 'large' && (
        <div className='header'>
          {t(name)}
          <span className='badge'>{data?.totalWorkers || 0}</span>
        </div>
      )}
      {/* <div className='widget-body photoTable'> */}
      <div className='body'>
        <Slider key={tabList?.length} {...settings}>
          {tabList?.map((el: any, i: number) => (
            <div className='card-container' key={el.sliderIndex}>
              <div className='card'>
                <div className='title' ref={tabRefs.current[i]}>
                  <span>{size === 'large' ? el.msName : '근로자 출입현황'}</span>
                </div>
                <div className={`count ${size === 'small' ? 'small' : ''}`}>
                  {el.count}
                  {/* <span className='countText'>명</span> */}
                </div>
              </div>
            </div>
          ))}
        </Slider>
      </div>
      {/* </div> */}
    </Root>
  );
};
